import React, { useMemo } from 'react';

import {
  DEFAULT_ASSET_FIELD_KEYS,
  mergeAssetFields
} from '../../utils/constant/defaultAssetFields';

function getDynamicValueByKey(row, key, assetFieldKeyById) {
  const arr = row?.dynamicFields;
  if (!Array.isArray(arr)) return '';
  const match = arr.find((df) => {
    const fieldId = df?.fieldId;
    if (fieldId && typeof fieldId === 'object' && typeof fieldId.key === 'string') {
      return fieldId.key === key;
    }
    if (typeof fieldId === 'string') {
      return assetFieldKeyById.get(fieldId) === key;
    }
    return false;
  });
  return match?.value ?? '';
}

function getAssetValueFromRow(row, key, assetFieldKeyById) {
  const dynamic = getDynamicValueByKey(row, key, assetFieldKeyById);
  if (dynamic !== undefined && dynamic !== null && String(dynamic).trim() !== '') {
    return String(dynamic);
  }
  const legacy = row?.[key];
  if (legacy !== undefined && legacy !== null && String(legacy).trim() !== '') {
    return String(legacy);
  }
  return '';
}

function humanizeLabel(labelOrKey = '') {
  const s = String(labelOrKey || '').trim();
  if (!s) return '';
  const spaced = s
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

const AssetsCard = ({ assetsData, assetFields = [], onEdit, canEdit = false, className = '' }) => {
  const fields = useMemo(() => {
    if (assetFields.length) return assetFields;
    const fromRow = (Array.isArray(assetsData?.dynamicFields) ? assetsData.dynamicFields : [])
      .map((df) => {
        const f = df?.fieldId;
        if (!f || typeof f !== 'object' || !f.key) return null;
        return { id: f._id || f.id || null, key: f.key, label: f.label || f.key };
      })
      .filter(Boolean);
    return mergeAssetFields(fromRow);
  }, [assetFields, assetsData]);

  const assetFieldKeyById = useMemo(() => {
    const m = new Map();
    fields.forEach((f) => {
      if (f?.id && f?.key) m.set(f.id, f.key);
    });
    return m;
  }, [fields]);

  const { standardItems, extraItems } = useMemo(() => {
    const standard = [];
    const extra = [];
    fields.forEach((f) => {
      const value = getAssetValueFromRow(assetsData, f.key, assetFieldKeyById);
      if (value === undefined || value === null || String(value).trim() === '') return;
      const item = {
        key: f.key,
        label: humanizeLabel(f.label || f.key),
        value
      };
      if (DEFAULT_ASSET_FIELD_KEYS.has(f.key)) {
        standard.push(item);
      } else {
        extra.push(item);
      }
    });
    return { standardItems: standard, extraItems: extra };
  }, [fields, assetsData, assetFieldKeyById]);

  const items = [...standardItems, ...extraItems];

  const renderItems = (list) => (
    <div className="row g-3">
      {list.map((it) => (
        <div className="col-12 col-md-6" key={it.key}>
          <div
            className="rounded-3 p-3 h-100"
            style={{
              background:
                'linear-gradient(180deg, rgba(248,249,250,1) 0%, rgba(255,255,255,1) 100%)',
              border: '1px solid rgba(0,0,0,0.06)'
            }}
          >
            <div className="text-muted small">{it.label}</div>
            <div className="fw-semibold text-dark mt-1">{it.value}</div>
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className={`card border-0 shadow-sm ep-info-card${className ? ` ${className}` : ''}`}>
      <div className="card-header bg-white py-3 d-flex align-items-center justify-content-between">
        <div className="ep-info-card__title">
          <span className="ep-info-card__title-icon" aria-hidden>
            <i className="bi bi-laptop" />
          </span>
          <h6 className="mb-0 fw-bold">Provided Assets</h6>
          {items.length > 0 && <span className="ep-info-card__count">{items.length}</span>}
        </div>
        {canEdit && typeof onEdit === 'function' && (
          <button type="button" className="btn btn-sm btn-outline-primary" onClick={onEdit}>
            <i className="icofont-edit me-1"></i>Edit
          </button>
        )}
      </div>
      <div className="card-body pt-2">
        {items.length === 0 ? (
          <div className="text-muted">No assets details available.</div>
        ) : (
          <>
            {standardItems.length > 0 && (
              <>
                <div className="text-muted small fw-semibold mb-2">Standard Assets</div>
                {renderItems(standardItems)}
              </>
            )}
            {extraItems.length > 0 && (
              <div className={standardItems.length > 0 ? 'mt-3' : ''}>
                <div className="text-muted small fw-semibold mb-2">Additional Fields</div>
                {renderItems(extraItems)}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AssetsCard;
